import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertCircle, ArrowLeft, DollarSign, Image, Info, SlidersHorizontal, Timer, Users } from 'lucide-react'
import { api, errorMessage } from '@/lib/api'
import { activeGroups, useOptionGroups } from '@/lib/options'
import type { Product, Tier } from '@/types'
import PageHeader from '@/components/ui/PageHeader'
import Button from '@/components/ui/Button'
import Badge from '@/components/ui/Badge'
import ConfirmDialog from '@/components/ui/ConfirmDialog'
import LogoLoader from '@/components/ui/LogoLoader'
import { useToast } from '@/components/ui/toast'
import { cn } from '@/lib/cn'
import {
  SECTIONS,
  draftFingerprint,
  draftFromProduct,
  draftProblems,
  newDraft,
  productPayload,
  revokePreviews,
  type Draft,
  type PatchDraft,
  type Section,
} from '@/components/admin/product/draft'
import DetailsSection from '@/components/admin/product/DetailsSection'
import ImagesSection from '@/components/admin/product/ImagesSection'
import PricingSection from '@/components/admin/product/PricingSection'
import OptionsSection from '@/components/admin/product/OptionsSection'
import CustomerPricesSection from '@/components/admin/product/CustomerPricesSection'
import UrgentSection from '@/components/admin/product/UrgentSection'

const NAV: Record<Section, { label: string; icon: typeof Info }> = {
  details: { label: 'Details', icon: Info },
  images: { label: 'Images', icon: Image },
  pricing: { label: 'Pricing', icon: DollarSign },
  options: { label: 'Options & add-ons', icon: SlidersHorizontal },
  customers: { label: 'Special customers', icon: Users },
  urgent: { label: 'Urgent', icon: Timer },
}

/**
 * Create or edit one product on its own page (2026-10-31: the modal had
 * outgrown itself). A section nav on the left shows one section at a time,
 * Printscape style, and one Save sends the whole product as the modal did.
 */
export default function ProductEditor() {
  const { id } = useParams()
  const navigate = useNavigate()
  const toast = useToast()
  const queryClient = useQueryClient()
  const [searchParams, setSearchParams] = useSearchParams()
  const requested = searchParams.get('section') as Section | null
  const section: Section = requested && SECTIONS.includes(requested) ? requested : 'details'

  const products = useQuery({
    queryKey: ['admin-products'],
    queryFn: async () => (await api.get<{ data: Product[] }>('/admin/products')).data.data,
  })
  const tiers = useQuery({
    queryKey: ['admin-pricing'],
    queryFn: async () => (await api.get<{ tiers: Tier[] }>('/admin/pricing')).data.tiers,
  })
  const optionGroups = useOptionGroups()
  // Hidden groups are left off the form; the server keeps a product's values in them.
  const groups = activeGroups(optionGroups.data)
  const tierList = tiers.data ?? []

  const product = id ? products.data?.find((p) => String(p.id) === id) : undefined

  const [draft, setDraft] = useState<Draft | null>(null)
  // The draft as loaded, to tell whether anything changed.
  const [baseline, setBaseline] = useState('')
  const [confirmLeave, setConfirmLeave] = useState(false)

  // Built once the product and tiers are in; a refetch never overwrites typing.
  useEffect(() => {
    if (draft || !tiers.data || (id && !product)) return
    const initial = product ? draftFromProduct(product, tiers.data) : newDraft(tiers.data)
    setDraft(initial)
    setBaseline(draftFingerprint(initial))
  }, [draft, id, product, tiers.data])

  // New picks hold object URLs; free them however the page is left.
  const latest = useRef<Draft | null>(null)
  latest.current = draft
  useEffect(
    () => () => {
      if (latest.current) revokePreviews(latest.current.images)
    },
    [],
  )

  const dirty = draft !== null && draftFingerprint(draft) !== baseline

  const save = useMutation({
    mutationFn: async (d: Draft) => {
      // Upload new picks first, sequentially so submit order is kept and only
      // one 4 MB body is in flight. A pick that already uploaded on a failed
      // save attempt is reused rather than uploaded again.
      const images: { id?: number; image_path: string; image_key: string | null }[] = []
      for (const item of d.images) {
        if (item.kind === 'existing') {
          images.push({ id: item.id, image_path: item.image_path, image_key: item.image_key })
        } else if (item.uploaded) {
          images.push(item.uploaded)
        } else {
          const form = new FormData()
          form.append('image', item.file)
          const uploaded = (await api.post<{ image_path: string; image_key: string }>('/admin/products/upload', form)).data
          item.uploaded = { image_path: uploaded.image_path, image_key: uploaded.image_key }
          images.push(item.uploaded)
        }
      }
      const payload = productPayload(d, images, groups, tierList.map((tier) => tier.id))
      return d.id
        ? (await api.patch(`/admin/products/${d.id}`, payload)).data
        : (await api.post('/admin/products', payload)).data
    },
    onSuccess: (res) => {
      for (const key of ['admin-products', 'products', 'admin-pricing', 'storefront-products', 'storefront-product', 'admin-option-groups']) {
        queryClient.invalidateQueries({ queryKey: [key] })
      }
      toast.success(res.message ?? 'Saved')
      // Back to the list, as the modal closed: the draft's new picks are
      // uploaded now, and saving them again would add them twice.
      navigate('/admin/products')
    },
    onError: (err) => toast.error('Save failed', errorMessage(err)),
  })

  // A reload or closed tab with unsaved typing asks first. In-app links are
  // not caught: BrowserRouter has no blocker, so Cancel is the guarded way out.
  useEffect(() => {
    if (!dirty || save.isPending) return
    const warn = (e: BeforeUnloadEvent) => e.preventDefault()
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [dirty, save.isPending])

  if (products.isLoading || tiers.isLoading || optionGroups.isLoading) return <LogoLoader />

  if (id && !product) {
    return (
      <div>
        <PageHeader title="Product not found" />
        <Link to="/admin/products" className="text-sm font-semibold text-primary hover:underline">
          Back to all products
        </Link>
      </div>
    )
  }

  if (!draft) return <LogoLoader />

  const patch: PatchDraft = (change) => setDraft((d) => (d ? { ...d, ...change } : d))
  const problems = draftProblems(draft, groups, tierList.map((tier) => tier.id))
  const problemCount = SECTIONS.reduce((sum, key) => sum + problems[key].length, 0)
  const firstProblem = SECTIONS.find((key) => problems[key].length > 0)

  const counts: Partial<Record<Section, number>> = {
    images: draft.images.length,
    options: groups.filter((group) => (draft.options[group.key] ?? []).length > 0).length,
    customers: draft.customer_prices.length,
  }

  const goTo = (key: Section) => {
    setSearchParams(key === 'details' ? {} : { section: key }, { replace: true })
    window.scrollTo({ top: 0 })
  }

  const leave = () => (dirty ? setConfirmLeave(true) : navigate('/admin/products'))

  return (
    <div>
      <Link
        to="/admin/products"
        onClick={(e) => {
          e.preventDefault()
          leave()
        }}
        className="mb-2 inline-flex items-center gap-1 text-xs font-semibold text-foreground/60 hover:text-primary"
      >
        <ArrowLeft className="size-3.5" /> All products
      </Link>
      <PageHeader
        title={
          <span className="flex flex-wrap items-center gap-2">
            {draft.id ? `Edit ${product?.name ?? draft.name}` : 'New product'}
            <Badge tone={draft.active ? 'success' : 'neutral'}>{draft.active ? 'Active' : 'Inactive'}</Badge>
          </span>
        }
        subtitle="Sizes, prices, garment options and urgent production for this product"
      />

      <div className="items-start gap-6 lg:grid lg:grid-cols-[240px_1fr]">
        {/* Positioning lives on this wrapper - .box forces its own position (see index.css gotcha) */}
        <div className="mb-6 lg:sticky lg:top-6 lg:mb-0">
          <nav aria-label="Product setup" className="box px-3 py-4">
            <p className="mb-2 ml-3 text-xs uppercase tracking-wider text-foreground/50">Product setup</p>
            <div className="flex gap-1 overflow-x-auto lg:flex-col lg:overflow-visible">
              {SECTIONS.map((key) => {
                const { label, icon: Icon } = NAV[key]
                const count = counts[key]
                const flagged = problems[key].length > 0
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => goTo(key)}
                    aria-current={section === key ? 'page' : undefined}
                    className={cn(
                      'flex shrink-0 items-center gap-2.5 whitespace-nowrap rounded-lg px-3 py-2 text-left text-sm transition-colors',
                      section === key ? 'bg-primary/10 font-medium text-primary' : 'text-foreground/70 hover:bg-foreground/5 hover:text-foreground',
                    )}
                  >
                    <Icon className="size-4 shrink-0" />
                    <span className="flex-1">{label}</span>
                    {count ? <span className="text-xs text-foreground/50">{count}</span> : null}
                    {flagged && <span className="size-2 shrink-0 rounded-full bg-danger" aria-label="Needs attention" />}
                  </button>
                )
              })}
            </div>
          </nav>
        </div>

        <div className="min-w-0">
          {problems[section].length > 0 && (
            <ul className="mb-4 space-y-1 rounded-xl border border-danger/60 bg-danger/10 px-4 py-3 text-xs text-danger">
              {problems[section].map((problem) => (
                <li key={problem} className="flex items-start gap-1.5">
                  <AlertCircle className="mt-px size-3.5 shrink-0" />
                  {problem}
                </li>
              ))}
            </ul>
          )}

          {section === 'details' && <DetailsSection draft={draft} patch={patch} />}
          {section === 'images' && <ImagesSection draft={draft} patch={patch} />}
          {section === 'pricing' && <PricingSection draft={draft} patch={patch} tiers={tierList} />}
          {section === 'options' && <OptionsSection draft={draft} patch={patch} groups={groups} tiers={tierList} />}
          {section === 'customers' && <CustomerPricesSection draft={draft} patch={patch} groups={groups} tiers={tierList} />}
          {section === 'urgent' && <UrgentSection draft={draft} patch={patch} />}

          {/* Positioning on the wrapper, as with the nav: .box is position: relative. */}
          <div className="sticky bottom-4 z-10 mt-6">
            <div className="box flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0 text-xs">
                {problemCount > 0 && firstProblem ? (
                  <button
                    type="button"
                    onClick={() => goTo(firstProblem)}
                    className="inline-flex items-center gap-1.5 text-left font-semibold text-danger hover:underline"
                  >
                    <AlertCircle className="size-4 shrink-0" />
                    {problemCount === 1 ? '1 thing to fix' : `${problemCount} things to fix`} before saving - see{' '}
                    {NAV[firstProblem].label}
                  </button>
                ) : (
                  <span className="text-muted">{dirty ? 'Unsaved changes' : 'No changes yet'}</span>
                )}
              </div>
              <div className="flex shrink-0 gap-2">
                <Button variant="outline" className="flex-1 sm:flex-none" onClick={leave}>
                  Cancel
                </Button>
                <Button
                  className="flex-1 sm:flex-none"
                  loading={save.isPending}
                  // Without the groups the save would send no garment options at all.
                  disabled={!dirty || problemCount > 0 || !optionGroups.data}
                  onClick={() => save.mutate(draft)}
                >
                  Save product
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={confirmLeave}
        onClose={() => setConfirmLeave(false)}
        onConfirm={() => navigate('/admin/products')}
        title="Discard your changes?"
        confirmLabel="Discard"
        cancelLabel="Keep editing"
        variant="danger"
      >
        Nothing you changed on this product has been saved.
      </ConfirmDialog>
    </div>
  )
}
